import { useEffect, useMemo, useState } from 'react'
import type { Activity, DiscoverBbox } from '@xingroute/schema'

export type ActivityFilter = 'all' | Activity
export type StatusFilter = 'all' | 'published' | 'draft'
export type SortMode = 'recent' | 'distance' | 'ascent'
export type ViewMode = 'list' | 'map'
export type RouteTypeFilter = 'all' | 'loop' | 'point_to_point' | 'out_and_back'
export type DifficultyFilter = 'all' | 'easy' | 'moderate' | 'hard' | 'expert'

export function useDiscoverFilters(): {
  activity: ActivityFilter
  setActivity: (next: ActivityFilter) => void
  status: StatusFilter
  setStatus: (next: StatusFilter) => void
  sort: SortMode
  setSort: (next: SortMode) => void
  view: ViewMode
  setView: (next: ViewMode) => void
  count: number
  setCount: (next: number) => void
  region: string
  setRegion: (next: string) => void
  routeType: RouteTypeFilter
  setRouteType: (next: RouteTypeFilter) => void
  difficulty: DifficultyFilter
  setDifficulty: (next: DifficultyFilter) => void
  bbox?: DiscoverBbox
  setBbox?: (next: DiscoverBbox | undefined) => void
  query: Record<string, string>
} {
  const initial = useMemo(readDiscoverQuery, [])
  const [activity, setActivity] = useState<ActivityFilter>(initial.activity)
  const [status, setStatus] = useState<StatusFilter>(initial.status)
  const [sort, setSort] = useState<SortMode>(initial.sort)
  const [view, setView] = useState<ViewMode>(initial.view)
  const [count, setCount] = useState(0)
  const [region, setRegion] = useState(initial.region)
  const [routeType, setRouteType] = useState<RouteTypeFilter>(initial.routeType)
  const [difficulty, setDifficulty] = useState<DifficultyFilter>(initial.difficulty)
  const [bbox, setBbox] = useState<DiscoverBbox | undefined>(initial.bbox)
  const query = useMemo(
    () =>
      toDiscoverQuery({
        activity,
        status,
        sort,
        view,
        region,
        routeType,
        difficulty,
        bbox,
      }),
    [activity, bbox, difficulty, region, routeType, sort, status, view],
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    for (const key of [
      'activity',
      'status',
      'sort',
      'view',
      'region',
      'route_type',
      'difficulty',
      'bbox',
    ]) {
      params.delete(key)
    }
    for (const [key, value] of Object.entries(query)) params.set(key, value)
    const next = `${window.location.pathname}${params.size ? `?${params.toString()}` : ''}${window.location.hash}`
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (next !== current) window.history.replaceState(window.history.state, '', next)
  }, [query])

  return {
    activity,
    setActivity,
    status,
    setStatus,
    sort,
    setSort,
    view,
    setView,
    count,
    setCount,
    region,
    setRegion,
    routeType,
    setRouteType,
    difficulty,
    setDifficulty,
    bbox,
    setBbox,
    query,
  }
}

function readDiscoverQuery(): {
  activity: ActivityFilter
  status: StatusFilter
  sort: SortMode
  view: ViewMode
  region: string
  routeType: RouteTypeFilter
  difficulty: DifficultyFilter
  bbox?: DiscoverBbox
} {
  if (typeof window === 'undefined') {
    return {
      activity: 'all',
      status: 'published',
      sort: 'recent',
      view: 'list',
      region: '',
      routeType: 'all',
      difficulty: 'all',
    }
  }
  const params = new URLSearchParams(window.location.search)
  return {
    activity: parseActivity(params.get('activity')),
    status: parseStatus(params.get('status')),
    sort: parseSort(params.get('sort')),
    view: parseView(params.get('view')),
    region: params.get('region')?.trim() ?? '',
    routeType: parseRouteType(params.get('route_type')),
    difficulty: parseDifficulty(params.get('difficulty')),
    bbox: parseBbox(params.get('bbox')),
  }
}

function toDiscoverQuery({
  activity,
  status,
  sort,
  view,
  region,
  routeType,
  difficulty,
  bbox,
}: {
  activity: ActivityFilter
  status: StatusFilter
  sort: SortMode
  view: ViewMode
  region: string
  routeType: RouteTypeFilter
  difficulty: DifficultyFilter
  bbox?: DiscoverBbox
}): Record<string, string> {
  const query: Record<string, string> = { status, sort }
  if (activity !== 'all') query.activity = activity
  if (view !== 'list') query.view = view
  const cleanRegion = region.trim()
  if (cleanRegion) query.region = cleanRegion
  if (routeType !== 'all') query.route_type = routeType
  if (difficulty !== 'all') query.difficulty = difficulty
  if (bbox) query.bbox = bbox.join(',')
  return query
}

function parseActivity(value: string | null): ActivityFilter {
  return value === 'hiking' || value === 'running' || value === 'cycling' ? value : 'all'
}

function parseStatus(value: string | null): StatusFilter {
  if (value === 'all' || value === 'draft') return value
  return 'published'
}

function parseSort(value: string | null): SortMode {
  return value === 'distance' || value === 'ascent' ? value : 'recent'
}

function parseView(value: string | null): ViewMode {
  return value === 'map' ? value : 'list'
}

function parseBbox(value: string | null): DiscoverBbox | undefined {
  if (!value) return undefined
  const parts = value.split(',').map((part) => Number(part))
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) return undefined
  return parts as DiscoverBbox
}

function parseRouteType(value: string | null): RouteTypeFilter {
  if (value === 'loop' || value === 'point_to_point' || value === 'out_and_back') return value
  return 'all'
}

function parseDifficulty(value: string | null): DifficultyFilter {
  if (value === 'easy' || value === 'moderate' || value === 'hard' || value === 'expert')
    return value
  return 'all'
}
